/**
 * TeacherLessonRunPage — the Live Classroom screen.
 *
 * Per Teacher Mode Build Spec §10 + §24B. The page implements the 10-phase
 * shape (Phase 1 ships 7 of 10):
 *   opening → warmup → vocab → pattern → repeat → recap → homework
 *
 * Phase indicator across the top, instructor speech bubble in the middle,
 * input row at the bottom. We POST the learner's text to /teacher/runs/.../turn
 * to get the instructor's reply. When `phase_done` flips true we POST
 * /advance and immediately re-fire the new phase's opener turn (action=true,
 * empty text) so the teacher takes the floor for the new step.
 *
 * Voice input + TTS playback are stubbed via a simple text input for now —
 * the existing `useRecorder` + /api/stt + /api/tts plumbing can be wired
 * later in Phase 2 of the build (just like LiveSessionPage already does).
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  CaretLeft, ChalkboardTeacher, ArrowRight, SpinnerGap,
  PaperPlaneTilt, SpeakerHigh, ClockCounterClockwise, CheckCircle,
} from '@phosphor-icons/react';
import { http, apiErrorMessage } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import TappableText from '@/components/TappableText';
import WordDefinitionSheet from '@/components/WordDefinitionSheet';

// Visible phase ordering for the progress dots — must mirror the backend's
// _PHASE_ORDER in routers/teacher_lessons.py minus the terminal "complete".
// Phase 2 of the build expands this from 7 → 10 phases.
const PHASE_DOTS = [
  { key: 'opening', label: 'Open', icon: '👋' },
  { key: 'warmup', label: 'Warm-up', icon: '🔥' },
  { key: 'vocab', label: 'Vocab', icon: '📖' },
  { key: 'pattern', label: 'Pattern', icon: '🧩' },
  { key: 'repeat', label: 'Repeat', icon: '🔁' },
  { key: 'controlled_practice', label: 'Drill', icon: '🎯' },
  { key: 'roleplay', label: 'Role', icon: '🎭' },
  { key: 'correction', label: 'Fix', icon: '✏️' },
  { key: 'recap', label: 'Recap', icon: '📝' },
  { key: 'homework', label: 'HW', icon: '🏠' },
  { key: 'exit_ticket', label: 'Ticket', icon: '🎟️' },
];

export default function TeacherLessonRunPage() {
  const { runId } = useParams();
  const navigate = useNavigate();
  const [run, setRun] = useState(null);
  const [lesson, setLesson] = useState(null);
  const [turns, setTurns] = useState([]);
  const [phase, setPhase] = useState('opening');
  const [completedPhases, setCompletedPhases] = useState([]);
  // Phase 3 — resolved instruction-language mode (e.g. "mostly_native").
  // Pulled from /teacher/runs/{id} response when present, falls back to ''.
  const [instructionMode, setInstructionMode] = useState('');
  const [userText, setUserText] = useState('');
  const [busy, setBusy] = useState(false);
  const [hint, setHint] = useState('');           // last reply_native_helper
  const [nextRepeat, setNextRepeat] = useState('');
  const [correction, setCorrection] = useState(null); // {original, corrected}
  const [error, setError] = useState('');
  const [completing, setCompleting] = useState(false);
  const [exitTicketResult, setExitTicketResult] = useState(null);
  const [exitTicketBusy, setExitTicketBusy] = useState(false);
  // Word lookup sheet state — every word in the teacher's bubble is
  // tappable and opens this sheet (same pattern as Free Speak).
  const { user } = useAuth();
  const [defWord, setDefWord] = useState(null);
  const openDef = (term, context) => setDefWord({ term, context });
  const closeDef = () => setDefWord(null);
  const turnsEndRef = useRef(null);

  // Initial load → opener turn from the teacher.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await http.get(`/teacher/runs/${runId}`);
        setRun(data.run);
        setLesson(data.lesson);
        setPhase(data.run?.phase || 'opening');
        setCompletedPhases(data.run?.completed_phases || []);
        setTurns(data.turns || []);
        setInstructionMode(data.instruction_language_mode || '');
        // If no turns yet, fire the teacher's opener (synthetic action turn).
        if (!data.turns?.length) {
          await fireTeacherTurn({ user_text: '', is_action: true });
        }
      } catch (e) {
        setError(apiErrorMessage(e));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runId]);

  useEffect(() => {
    turnsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [turns.length]);

  /**
   * Send a turn to the instructor. If the response sets `phase_done`, we
   * advance the phase and immediately fire a fresh opener turn for the
   * new phase so the teacher takes the floor.
   */
  async function fireTeacherTurn({ user_text, is_action }) {
    setBusy(true);
    setError('');
    try {
      // 1. If the learner typed something, append a synthetic 'user' bubble
      //    immediately so the UI feels responsive.
      if (user_text && !is_action) {
        setTurns((prev) => [
          ...prev,
          { id: `local-${Date.now()}`, speaker: 'user', text: user_text, phase },
        ]);
      }
      const { data } = await http.post(`/teacher/runs/${runId}/turn`, {
        user_text, is_action,
      });
      // 2. Append the teacher reply to the visible transcript.
      setTurns((prev) => [
        ...prev,
        {
          id: `t-${Date.now()}`,
          speaker: 'teacher',
          text: data.reply_target,
          translated_text: data.reply_native_helper,
          phase: data.phase,
        },
      ]);
      setHint(data.reply_native_helper || '');
      setNextRepeat(data.next_repeat_phrase || '');
      // Phase 2 — capture corrections so the dedicated card can show them.
      const correctionPair = data.correction;
      if (correctionPair?.original && correctionPair?.corrected) {
        setCorrection(correctionPair);
      } else if (data.phase !== 'correction') {
        // Clear stale correction once we leave the phase.
        setCorrection(null);
      }

      // 3. If the LLM signals phase_done, advance + fire next-phase opener.
      if (data.phase_done) {
        const adv = await http.post(`/teacher/runs/${runId}/advance`, {});
        setPhase(adv.data.phase);
        setCompletedPhases(adv.data.completed_phases || []);
        if (adv.data.phase === 'complete') {
          await completeRun();
          return;
        }
        // Phase 2 — exit_ticket has its own UI gate (ExitTicketCard).
        // Halt the auto-advance cascade so the card can render and the
        // learner controls when the run finishes via /exit-ticket submit.
        if (adv.data.phase === 'exit_ticket') {
          return;
        }
        // After advancing, fire a synthetic teacher opener for the new phase.
        await new Promise((r) => setTimeout(r, 250));
        await fireTeacherTurn({ user_text: '', is_action: true });
      }
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  async function completeRun() {
    setCompleting(true);
    try {
      await http.post(`/teacher/runs/${runId}/complete`, {});
      navigate(`/teacher/recap/${runId}`, { replace: true });
    } catch (e) {
      setError(apiErrorMessage(e));
      setCompleting(false);
    }
  }

  async function onSend() {
    const text = userText.trim();
    if (!text || busy) return;
    setUserText('');
    await fireTeacherTurn({ user_text: text, is_action: false });
  }

  /** "Skip this step" — manual phase advance. */
  async function skipPhase() {
    if (busy) return;
    setBusy(true);
    try {
      const adv = await http.post(`/teacher/runs/${runId}/advance`, {});
      setPhase(adv.data.phase);
      setCompletedPhases(adv.data.completed_phases || []);
      if (adv.data.phase === 'complete') {
        await completeRun();
        return;
      }
      // Phase 2 — exit_ticket has its own UI gate (ExitTicketCard). Stop
      // here so the learner controls completion via Submit/Skip.
      if (adv.data.phase === 'exit_ticket') {
        return;
      }
      await fireTeacherTurn({ user_text: '', is_action: true });
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  if (error && !lesson) {
    return (
      <div className="lf-shell px-6 py-10" data-testid="teacher-run-error">
        <div className="lf-card p-5 text-center">
          <div className="text-base font-semibold">Couldn't load this lesson</div>
          <p className="mt-1 text-sm text-[var(--lf-text-muted)]">{error}</p>
          <Link to="/home" className="lf-btn lf-btn-primary mt-4 inline-flex">Back to Home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="lf-shell flex flex-col px-5 py-5" data-testid="teacher-run-page">
      {/* Top bar with lesson title + phase progress */}
      <div className="flex items-center gap-2">
        <Link
          to="/home"
          className="grid h-10 w-10 place-items-center rounded-full border border-[var(--lf-border)] bg-[var(--lf-surface)]"
          data-testid="teacher-run-back"
        >
          <CaretLeft size={18} />
        </Link>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-[var(--lf-text-muted)]">
            Teacher mode · {lesson?.cefr_band}
          </div>
          <div className="text-sm font-bold truncate" data-testid="teacher-run-title">
            {lesson?.title || 'Lesson'}
          </div>
          {instructionMode ? (
            <InstructionModeBadge mode={instructionMode} />
          ) : null}
        </div>
        <button
          onClick={skipPhase}
          disabled={busy}
          className="text-xs text-[var(--lf-text-muted)] underline-offset-2 hover:underline disabled:opacity-40"
          data-testid="teacher-run-skip-btn"
        >
          Next step
        </button>
      </div>

      <PhaseDots phase={phase} completed={completedPhases} />

      <PhaseBanner phase={phase} lesson={lesson} />

      {/* Transcript */}
      <div className="mt-4 flex-1 overflow-y-auto space-y-3 pb-4" data-testid="teacher-run-transcript" role="log" aria-live="polite" aria-atomic="false" aria-label="Teacher lesson transcript">
        {turns.map((t) => (
          <Bubble key={t.id || `${t.speaker}-${t.text?.slice(0,8)}`} turn={t} phase={phase} onWordTap={openDef} />
        ))}
        {busy ? (
          <div className="flex items-center gap-2 text-xs text-[var(--lf-text-muted)] pl-2" data-testid="teacher-run-thinking">
            <SpinnerGap size={14} className="animate-spin" /> Teacher is thinking…
          </div>
        ) : null}
        <div ref={turnsEndRef} />
      </div>

      {/* Phase 2 — Correction card (shows the original→corrected pair) */}
      {phase === 'correction' && correction?.corrected ? (
        <div
          className="mb-2 rounded-2xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-900"
          data-testid="teacher-run-correction-card"
        >
          <div className="text-[10px] uppercase tracking-widest text-rose-700 font-semibold">
            Correction
          </div>
          {correction.original ? (
            <div className="mt-1 line-through text-rose-500 text-xs">
              {correction.original}
            </div>
          ) : null}
          <div className="mt-1 font-semibold">{correction.corrected}</div>
        </div>
      ) : null}

      {/* Hint strip */}
      {hint ? (
        <div className="mb-2 rounded-2xl border border-[var(--lf-border)] bg-[var(--lf-surface)] p-2 text-xs text-[var(--lf-text-muted)]" data-testid="teacher-run-hint">
          <SpeakerHigh size={12} className="inline mr-1" /> {hint}
        </div>
      ) : null}

      {nextRepeat ? (
        <div className="mb-2 rounded-2xl border border-emerald-200 bg-emerald-50 p-2.5 text-sm text-emerald-900" data-testid="teacher-run-repeat-hint">
          <strong>Repeat:</strong> {nextRepeat}
        </div>
      ) : null}

      {error ? (
        <div className="mb-2 text-xs text-[var(--lf-destructive)]" data-testid="teacher-run-error-msg">
          {error}
        </div>
      ) : null}

      {/* Phase 2 — Exit Ticket card replaces the regular input row */}
      {phase === 'exit_ticket' ? (
        <ExitTicketCard
          run={run}
          lesson={lesson}
          result={exitTicketResult}
          busy={exitTicketBusy}
          onSubmit={async (heard) => {
            const target = lesson?.exit_ticket || run?.exit_ticket_text || '';
            if (!target.trim()) return;
            setExitTicketBusy(true);
            setError('');
            try {
              const { data } = await http.post(
                `/teacher/runs/${runId}/exit-ticket`,
                { target, heard },
              );
              setExitTicketResult(data);
              if (data.passed) {
                // On pass, finish the lesson and route to recap.
                await new Promise((r) => setTimeout(r, 1100));
                await completeRun();
              }
            } catch (e) {
              setError(apiErrorMessage(e));
            } finally {
              setExitTicketBusy(false);
            }
          }}
          onSkip={completeRun}
        />
      ) : (
        /* Default input row */
        <div className="flex items-end gap-2 border-t border-[var(--lf-border)] pt-3">
          <textarea
            value={userText}
            onChange={(e) => setUserText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                onSend();
              }
            }}
            rows={1}
            placeholder={phaseToPlaceholder(phase)}
            className="flex-1 resize-none rounded-2xl border border-[var(--lf-border)] bg-[var(--lf-surface)] p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--lf-primary)]"
            disabled={busy || completing}
            data-testid="teacher-run-input"
          />
          <button
          onClick={onSend}
          disabled={busy || completing || !userText.trim()}
          className="lf-btn lf-btn-primary px-4"
          data-testid="teacher-run-send-btn"
        >
          {busy ? <SpinnerGap size={16} className="animate-spin" /> : <PaperPlaneTilt size={16} weight="fill" />}
        </button>
      </div>
      )}

      <button
        onClick={completeRun}
        disabled={busy || completing}
        className="mt-3 text-xs text-[var(--lf-text-muted)] underline-offset-2 hover:underline disabled:opacity-40"
        data-testid="teacher-run-end-btn"
      >
        End lesson early
      </button>

      {/* Tap any word in the teacher's bubble → open this sheet. Matches
          the Free-Speak pattern so learners have the same dictionary
          behaviour across both surfaces. */}
      <WordDefinitionSheet
        open={!!defWord}
        term={defWord?.term}
        context={defWord?.context}
        onClose={closeDef}
        practiceDirection={user?.practice_direction}
      />
    </div>
  );
}

/**
 * Phase 3 — instruction language badge. Shown below the lesson title in
 * the Run page header. Tells the learner what to expect re: native vs.
 * target language balance during the lesson.
 */
function InstructionModeBadge({ mode }) {
  const map = {
    native:         { label: '100% native', tone: 'bg-slate-100 text-slate-800 border-slate-200' },
    mostly_native:  { label: '80/20 native', tone: 'bg-slate-100 text-slate-800 border-slate-200' },
    mixed:          { label: '50/50 mixed', tone: 'bg-amber-100 text-amber-900 border-amber-200' },
    mostly_target:  { label: '80% target', tone: 'bg-emerald-100 text-emerald-900 border-emerald-200' },
    full_immersion: { label: '100% target', tone: 'bg-emerald-100 text-emerald-900 border-emerald-200' },
  };
  const cfg = map[mode] || map.mostly_native;
  return (
    <div
      className={`mt-0.5 inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[9px] font-semibold ${cfg.tone}`}
      data-testid="teacher-run-instruction-mode-badge"
    >
      🌐 {cfg.label}
    </div>
  );
}

function PhaseDots({ phase, completed }) {
  const activeIdx = PHASE_DOTS.findIndex((p) => p.key === phase);
  const activeStep = activeIdx >= 0 ? activeIdx + 1 : 0;
  return (
    <div className="mt-3" data-testid="teacher-run-phase-dots">
      {/* Compact dots — 11 pills don't fit a label each at 420px wide, so we
          show pure progress bars and the active phase label below.        */}
      <div className="flex items-center gap-1">
        {PHASE_DOTS.map((p) => {
          const isDone = completed.includes(p.key);
          const isActive = phase === p.key;
          return (
            <div
              key={p.key}
              className={`h-1.5 flex-1 rounded-full transition-colors ${
                isDone
                  ? 'bg-[var(--lf-primary)]'
                  : isActive
                  ? 'bg-[#FFE2C8] ring-2 ring-[var(--lf-primary)]/40'
                  : 'bg-[var(--lf-border)]'
              }`}
              data-testid={`teacher-run-phase-${p.key}`}
              aria-current={isActive}
            />
          );
        })}
      </div>
      <div className="mt-1.5 flex items-center justify-between text-[10px] text-[var(--lf-text-muted)]">
        <span data-testid="teacher-run-phase-step">
          Step {activeStep} of {PHASE_DOTS.length}
        </span>
        <span className="font-bold text-[var(--lf-primary)] uppercase tracking-wider" data-testid="teacher-run-phase-label">
          {PHASE_DOTS[activeIdx]?.icon} {PHASE_DOTS[activeIdx]?.label}
        </span>
      </div>
    </div>
  );
}

/**
 * Phase 2 — phase-specific context banner above the transcript.
 *   - roleplay: surfaces the role context so the learner remembers who they're
 *     speaking with (e.g. "Cafe cashier").
 *   - controlled_practice / correction: gentle reminders so the page mode is
 *     visually obvious.
 */
function PhaseBanner({ phase, lesson }) {
  if (phase === 'roleplay' && lesson?.roleplay_context) {
    return (
      <div
        className="mt-3 rounded-2xl border border-violet-200 bg-violet-50 p-2.5 text-xs text-violet-900"
        data-testid="teacher-run-roleplay-banner"
      >
        <span className="font-bold uppercase tracking-widest text-[10px] text-violet-700">
          🎭 Roleplay
        </span>{' '}
        <span className="capitalize">
          {lesson.roleplay_context.replace(/_/g, ' ')}
        </span>
      </div>
    );
  }
  if (phase === 'controlled_practice') {
    return (
      <div
        className="mt-3 rounded-2xl border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-900"
        data-testid="teacher-run-drill-banner"
      >
        <span className="font-bold uppercase tracking-widest text-[10px] text-amber-700">
          🎯 Drill
        </span>{' '}
        Substitution / transformation — try the pattern with new words.
      </div>
    );
  }
  if (phase === 'correction') {
    return (
      <div
        className="mt-3 rounded-2xl border border-rose-200 bg-rose-50 p-2.5 text-xs text-rose-900"
        data-testid="teacher-run-correction-banner"
      >
        <span className="font-bold uppercase tracking-widest text-[10px] text-rose-700">
          ✏️ Correction
        </span>{' '}
        We'll work on one thing — the rest is great.
      </div>
    );
  }
  return null;
}

/**
 * Phase 2 — Exit Ticket scoring card. Replaces the regular input row when
 * `phase === "exit_ticket"`. Submits the learner's attempt to
 * `/teacher/runs/.../exit-ticket`. On pass (similarity ≥0.6) the run
 * auto-completes and routes to the recap. On fail it shows the score, the
 * correct phrase, and a "weak phrase" suggestion to drill before retry.
 */
function ExitTicketCard({ run, lesson, result, busy, onSubmit, onSkip }) {
  const [text, setText] = useState('');
  const target = lesson?.exit_ticket || run?.exit_ticket_text || 'Show me what you learned today.';
  return (
    <div
      className="mt-2 rounded-2xl border-2 border-[var(--lf-primary)] bg-gradient-to-br from-[#FFF7EE] to-white p-4"
      data-testid="teacher-run-exit-ticket-card"
    >
      <div className="text-[10px] uppercase tracking-widest text-[var(--lf-primary)] font-bold flex items-center gap-1">
        🎟️ Exit ticket
      </div>
      <p className="mt-1 text-sm font-semibold leading-snug" data-testid="teacher-run-exit-ticket-prompt">
        {target}
      </p>
      {result ? (
        <div
          className={`mt-3 rounded-xl border p-2.5 text-sm ${
            result.passed
              ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
              : 'border-rose-200 bg-rose-50 text-rose-900'
          }`}
          data-testid="teacher-run-exit-ticket-result"
        >
          <div className="font-bold">
            {result.passed ? '✓ Nailed it' : '✗ Almost there'}{' '}
            <span className="text-xs font-medium ml-1">
              ({Math.round(result.score * 100)}% match)
            </span>
          </div>
          {!result.passed && result.weak_phrase ? (
            <div className="mt-1 text-xs">
              Practice this first:{' '}
              <span className="font-semibold">{result.weak_phrase}</span>
            </div>
          ) : null}
        </div>
      ) : null}
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={2}
        placeholder="Type or speak your answer…"
        className="mt-3 w-full resize-none rounded-2xl border border-[var(--lf-border)] bg-white p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--lf-primary)]"
        disabled={busy}
        data-testid="teacher-run-exit-ticket-input"
      />
      <div className="mt-3 flex gap-2">
        <button
          onClick={() => onSubmit(text)}
          disabled={busy || !text.trim()}
          className="lf-btn lf-btn-primary flex-1"
          data-testid="teacher-run-exit-ticket-submit"
        >
          {busy ? <SpinnerGap size={14} className="animate-spin" /> : 'Submit'}
        </button>
        <button
          onClick={onSkip}
          disabled={busy}
          className="lf-btn lf-btn-ghost"
          data-testid="teacher-run-exit-ticket-skip"
        >
          Skip
        </button>
      </div>
    </div>
  );
}

function Bubble({ turn, phase, onWordTap }) {
  const isTeacher = turn.speaker === 'teacher' || turn.speaker === 'ai';
  // Phase 2 — tint the teacher bubble subtly during roleplay so the learner
  // remembers the teacher is "in character" right now.
  const turnPhase = turn.phase || phase;
  const isRoleplay = turnPhase === 'roleplay';
  if (isTeacher) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-start gap-2"
      >
        <div className={`grid h-7 w-7 flex-shrink-0 place-items-center rounded-full text-white ${
          isRoleplay ? 'bg-violet-600' : 'bg-[var(--lf-primary)]'
        }`}>
          {isRoleplay ? (
            <span className="text-xs">🎭</span>
          ) : (
            <ChalkboardTeacher size={14} weight="duotone" />
          )}
        </div>
        <div
          className={`rounded-2xl rounded-tl-sm p-3 max-w-[85%] border ${
            isRoleplay
              ? 'bg-violet-50 border-violet-200'
              : 'bg-[var(--lf-surface)] border-[var(--lf-border)]'
          }`}
          data-testid={isRoleplay ? 'teacher-bubble-roleplay' : 'teacher-bubble'}
        >
          {/* Every word in the teacher's reply is tappable → opens a
              dictionary sheet with definition, pronunciation + examples.
              Before this, only manual Ask-Teacher could define a single
              word; now the full example sentence is fully interactive,
              matching Free Speak's Tappable behavior. */}
          <div className="text-sm text-[var(--lf-text)]">
            {onWordTap ? (
              <TappableText text={turn.text || ''} onWordTap={onWordTap} context={turn.text || ''} />
            ) : (
              turn.text
            )}
          </div>
          {turn.translated_text ? (
            <div className="mt-1 text-xs text-[var(--lf-text-muted)]">{turn.translated_text}</div>
          ) : null}
        </div>
      </motion.div>
    );
  }
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex justify-end"
    >
      <div className="rounded-2xl rounded-tr-sm bg-[#FFE2C8] p-3 max-w-[85%] text-sm" data-testid="learner-bubble">
        {turn.text}
      </div>
    </motion.div>
  );
}

function phaseToPlaceholder(phase) {
  switch (phase) {
    case 'opening': return 'Type "ready" or just press send to begin…';
    case 'warmup': return 'Answer the warm-up question…';
    case 'vocab': return 'Repeat the new word, or ask a question…';
    case 'pattern': return 'Try the pattern with your own word…';
    case 'repeat': return 'Repeat the phrase, or type "again"…';
    case 'controlled_practice': return 'Substitute a new word into the pattern…';
    case 'roleplay': return 'Reply in character (you can stay simple)…';
    case 'correction': return 'Repeat the correct version once…';
    case 'recap': return 'Tell me one thing you learned…';
    case 'homework': return 'Type "got it" or ask one last question…';
    default: return 'Reply to the teacher…';
  }
}
